import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';

function AdminSettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/AdminConfig/current');
      setConfig(res.data.data);
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig({ ...config, [field]: value === '' ? '' : Number(value) });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/AdminConfig/update', {
        joiningAmount: config.joiningAmount,           // Plan A
        planBJoiningAmount: config.planBJoiningAmount, // Plan B
        processingCharge: config.processingCharge,
        levelIncomeL1: config.levelIncomeL1,
        levelIncomeL2: config.levelIncomeL2,
        levelIncomeL3: config.levelIncomeL3,
        passiveIncomeDaily: config.passiveIncomeDaily,
        passiveIncomeMinMembers: config.passiveIncomeMinMembers,
        passiveIncomeMaxDaily: config.passiveIncomeMaxDaily,
        giftAmount: config.giftAmount,
        giftEveryMembers: config.giftEveryMembers,
        monthlyRecharge: config.monthlyRecharge,
        loanAmount: config.loanAmount,
        loanMonthlyEmi: config.loanMonthlyEmi,
        loanMinDirectMembers: config.loanMinDirectMembers,
      });
      toast.success('Settings saved successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  const Field = ({ label, field }) => (
    <div>
      <label className="text-sm text-gray-600">{label}</label>
      <input
        type="number"
        value={config[field] ?? ''}
        onChange={(e) => handleChange(field, e.target.value)}
        className="w-full mt-1 border rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Admin Settings</h1>
            <p className="text-gray-500 text-sm">System Configuration</p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="text-sm bg-gray-200 px-4 py-2 rounded-lg"
          >
            ← Back
          </button>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow space-y-6">
          {/* Joining — Plan A / Plan B */}
          <div>
            <h3 className="font-bold text-gray-700 mb-3">💰 Joining Plans</h3>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Plan A Amount (₹)" field="joiningAmount" />
              <Field label="Plan B Amount (₹)" field="planBJoiningAmount" />
              <Field label="Processing Charge (₹)" field="processingCharge" />
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Plan A = basic joining · Plan B = higher package (same benefits logic)
            </p>
          </div>

          {/* Level Income */}
          <div>
            <h3 className="font-bold text-gray-700 mb-3">📊 Level Income</h3>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Level 1 (₹)" field="levelIncomeL1" />
              <Field label="Level 2 (₹)" field="levelIncomeL2" />
              <Field label="Level 3 (₹)" field="levelIncomeL3" />
            </div>
          </div>

          {/* Passive */}
          <div>
            <h3 className="font-bold text-gray-700 mb-3">📈 Passive Income</h3>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Per Day (₹)" field="passiveIncomeDaily" />
              <Field label="Min Members" field="passiveIncomeMinMembers" />
              <Field label="Max Daily (₹)" field="passiveIncomeMaxDaily" />
            </div>
          </div>

          {/* Gift */}
          <div>
            <h3 className="font-bold text-gray-700 mb-3">🎁 Gift Income</h3>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Gift Amount (₹)" field="giftAmount" />
              <Field label="Every X Members" field="giftEveryMembers" />
            </div>
          </div>

          {/* Recharge & Loan */}
          <div>
            <h3 className="font-bold text-gray-700 mb-3">⚡ Recharge & Loan</h3>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Monthly Recharge (₹)" field="monthlyRecharge" />
              <Field label="Loan Amount (₹)" field="loanAmount" />
              <Field label="Loan EMI (₹)" field="loanMonthlyEmi" />
              <Field label="Loan Min Members" field="loanMinDirectMembers" />
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminSettings;